/**
 * Handing Cmd+K to the Shell, and only when the Shell is really there.
 *
 * WHY THIS EXISTS. When POE is embedded in LA-AI's External inbox it is a
 * CROSS-ORIGIN iframe, so a keydown inside it is delivered to this document and
 * never reaches LA-AI's command palette, which listens on the parent window.
 * The shortcut looked broken in External and only came alive after the user
 * clicked LA-AI chrome, because that moved focus back to the parent. LA-AI
 * cannot read keys in our document - that is the same-origin policy - so we
 * have to hand the keystroke over deliberately.
 *
 * THE RULE, AND IT IS THE POINT OF THIS MODULE:
 *
 *   embedded in the Shell -> the Shell's palette opens, ours stays shut
 *   standalone POE        -> our own palette opens, nothing is posted anywhere
 *
 * Standalone is the first-class POE experience per Rick's directive, and
 * nothing here may weaken it. So the gate is `useEmbeddedMode`, which is
 * pessimistic by construction: `isEmbedded` stays false until a parent has
 * identified itself with `shell:hello`, and a top-level window never even
 * listens. Someone using POE directly cannot reach this path, and someone who
 * frames POE without completing the handshake cannot either.
 *
 * The message is posted to the handshake's exact origin, never "*". A wildcard
 * would hand a keystroke to whatever page happened to frame us; `shell:poe-ready`
 * can use "*" because it carries nothing, this cannot.
 */

export const COMMAND_PALETTE_MESSAGE = 'shell:command-palette';

/** The shape `useEmbeddedMode()` returns; only the two fields we gate on. */
export interface ShellEmbedState {
  isEmbedded: boolean;
  parentOrigin: string | null;
}

/**
 * Should this Cmd+K go to the Shell instead of opening our own palette?
 *
 * Requires BOTH a completed handshake and a concrete origin to send to. The
 * origin check is not redundant defensiveness: without it a malformed handshake
 * would leave us posting to a null target, and the honest fallback in every
 * uncertain case is "this is standalone POE, open our own palette".
 */
export function shouldForwardToShell(state: ShellEmbedState | null | undefined): boolean {
  return Boolean(state?.isEmbedded && state.parentOrigin);
}

/**
 * Hand the keystroke to the Shell. Returns true when it was forwarded, so the
 * caller knows to leave POE's own palette closed.
 *
 * Returns false - and posts nothing at all - in every standalone case.
 */
export function forwardCommandPaletteToShell(
  state: ShellEmbedState | null | undefined,
  parent: Pick<Window, 'postMessage'> | null = typeof window === 'undefined' ? null : window.parent,
): boolean {
  if (!shouldForwardToShell(state) || !parent) return false;
  try {
    parent.postMessage({ type: COMMAND_PALETTE_MESSAGE }, state!.parentOrigin as string);
  } catch {
    // A parent that refuses the message is not a reason to swallow the user's
    // keystroke: report not-forwarded and let our own palette open.
    return false;
  }
  return true;
}
